import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Modal, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useCreateRecipe } from '@/hooks/useRecipes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { RecipeImagePicker, type SelectedRecipeImage } from '@/components/RecipeImagePicker';
import { Brand, Neutrals } from '@/constants/theme';
import { clearImportedRecipe, getImportedRecipe } from '@/lib/import-recipe-store';
import { trackEvent } from '@/lib/analytics';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

function getDomainFromUrl(url: string): string {
  try {
    const host = new URL(url).hostname;
    return host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export default function AddRecipe() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { createRecipe, loading } = useCreateRecipe();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [servings, setServings] = useState('4');
  const [prepTime, setPrepTime] = useState('');
  const [cookTime, setCookTime] = useState('');
  const [ingredients, setIngredients] = useState<string[]>(['']);
  const [instructions, setInstructions] = useState<string[]>(['']);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [selectedImage, setSelectedImage] = useState<SelectedRecipeImage>(null);
  const [importedImageUrl, setImportedImageUrl] = useState<string | null>(null);
  const [importedSourceUrl, setImportedSourceUrl] = useState<string | null>(null);
  const [showImportedBanner, setShowImportedBanner] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalTitle, setAlertModalTitle] = useState('');
  const [alertModalMessage, setAlertModalMessage] = useState('');

  useEffect(() => {
    const imported = getImportedRecipe();
    clearImportedRecipe();
    if (!imported) return;
    setTitle(imported.title ?? '');
    setDescription(imported.description ?? '');
    setServings(String(imported.servings ?? 4));
    setPrepTime(imported.prepTimeMinutes != null ? String(imported.prepTimeMinutes) : '');
    setCookTime(imported.cookTimeMinutes != null ? String(imported.cookTimeMinutes) : '');
    setIngredients(
      imported.ingredients?.length
        ? imported.ingredients.map((i) => i.raw)
        : ['']
    );
    setInstructions(imported.instructions?.length ? imported.instructions : ['']);
    setTags(imported.tags ?? []);
    setImportedImageUrl(imported.imageUrl ?? null);
    setImportedSourceUrl(imported.sourceUrl ?? null);
    setShowImportedBanner(true);
  }, []);

  const showAlert = useCallback((title: string, message: string) => {
    setAlertModalTitle(title);
    setAlertModalMessage(message);
    setShowAlertModal(true);
  }, []);

  const handleAddIngredient = () => {
    setIngredients([...ingredients, '']);
  };

  const handleRemoveIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const handleIngredientChange = (index: number, value: string) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = value;
    setIngredients(newIngredients);
  };

  const handleAddInstruction = () => {
    setInstructions([...instructions, '']);
  };

  const handleRemoveInstruction = (index: number) => {
    setInstructions(instructions.filter((_, i) => i !== index));
  };

  const handleInstructionChange = (index: number, value: string) => {
    const newInstructions = [...instructions];
    newInstructions[index] = value;
    setInstructions(newInstructions);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      showAlert('Error', 'Please enter a recipe title');
      return;
    }

    const filteredIngredients = ingredients.filter(i => i.trim());
    const filteredInstructions = instructions.filter(i => i.trim());

    if (filteredIngredients.length === 0) {
      showAlert('Error', 'Please add at least one ingredient');
      return;
    }

    if (filteredInstructions.length === 0) {
      showAlert('Error', 'Please add at least one instruction');
      return;
    }

    try {
      const images = importedImageUrl
        ? [importedImageUrl]
        : selectedImage
          ? [selectedImage]
          : [];
      const recipe = await createRecipe({
        title: title.trim(),
        description: description.trim() || undefined,
        ingredients: filteredIngredients,
        instructions: filteredInstructions,
        servings: parseInt(servings) || 4,
        prepTime: parseInt(prepTime) || 0,
        cookTime: parseInt(cookTime) || 0,
        images,
        tags,
        sourceUrl: importedSourceUrl ?? undefined,
      });
      if (recipe) {
        trackEvent('Recipe created', { recipe_id: recipe.id });
      }
      router.back();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save recipe. Please try again.';
      showAlert('Error', message);
    }
  };

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.content, { paddingTop: 16 + insets.top }]}
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>
          <Text style={styles.title}>Add New Recipe</Text>
        </View>

        {showImportedBanner && importedSourceUrl ? (
          <View style={styles.importedBanner}>
            <Ionicons name="link" size={18} color={Brand.primary} />
            <Text style={styles.importedBannerText}>
              Imported from {getDomainFromUrl(importedSourceUrl)}
            </Text>
            <TouchableOpacity
              onPress={() => setShowImportedBanner(false)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              style={styles.importedBannerDismiss}
            >
              <Ionicons name="close" size={20} color={Neutrals.textSecondary} />
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Basic Info */}
        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Basic Information</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            <RecipeImagePicker value={selectedImage} onChange={setSelectedImage} />

            <Input
              label="Recipe Title *"
              value={title}
              onChangeText={setTitle}
              placeholder="e.g., Thai Green Curry"
            />

            <Input
              label="Description"
              value={description}
              onChangeText={setDescription}
              placeholder="Brief description of your recipe"
              multiline
              numberOfLines={3}
            />

            <View style={styles.row}>
              <View style={styles.rowItem}>
                <Input
                  label="Servings"
                  value={servings}
                  onChangeText={setServings}
                  keyboardType="numeric"
                />
              </View>
              <View style={styles.rowItem}>
                <Input
                  label="Prep Time (min)"
                  value={prepTime}
                  onChangeText={setPrepTime}
                  keyboardType="numeric"
                  placeholder="15"
                />
              </View>
              <View style={styles.rowItem}>
                <Input
                  label="Cook Time (min)"
                  value={cookTime}
                  onChangeText={setCookTime}
                  keyboardType="numeric"
                  placeholder="30"
                />
              </View>
            </View>
          </CardContent>
        </Card>

        {/* Ingredients */}
        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Ingredients</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            {ingredients.map((ingredient, index) => (
              <View key={index} style={styles.listItem}>
                <Input
                  value={ingredient}
                  onChangeText={(value) => handleIngredientChange(index, value)}
                  placeholder="e.g., 2 cups flour"
                  containerStyle={styles.listItemInput}
                />
                {ingredients.length > 1 && (
                  <TouchableOpacity
                    onPress={() => handleRemoveIngredient(index)}
                    style={styles.removeButton}
                  >
                    <Ionicons name="close-circle" size={24} color={Brand.primaryDark} />
                  </TouchableOpacity>
                )}
              </View>
            ))}
            <TouchableOpacity onPress={handleAddIngredient} style={styles.addButton}>
              <Ionicons name="add" size={16} color={Brand.primary} />
              <Text style={styles.addButtonText}>Add Ingredient</Text>
            </TouchableOpacity>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Instructions</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            {instructions.map((instruction, index) => (
              <View key={index} style={styles.instructionItem}>
                <View style={styles.instructionNumber}>
                  <Text style={styles.instructionNumberText}>{index + 1}</Text>
                </View>
                <Input
                  value={instruction}
                  onChangeText={(value) => handleInstructionChange(index, value)}
                  placeholder="Describe this step..."
                  multiline
                  numberOfLines={2}
                  containerStyle={styles.instructionInput}
                />
                {instructions.length > 1 && (
                  <TouchableOpacity
                    onPress={() => handleRemoveInstruction(index)}
                    style={styles.removeButton}
                  >
                    <Ionicons name="close-circle" size={24} color={Brand.primaryDark} />
                  </TouchableOpacity>
                )}
              </View>
            ))}
            <TouchableOpacity onPress={handleAddInstruction} style={styles.addButton}>
              <Ionicons name="add" size={16} color={Brand.primary} />
              <Text style={styles.addButtonText}>Add Step</Text>
            </TouchableOpacity>
          </CardContent>
        </Card>

        {/* Tags */}
        <Card style={styles.card}>
          <CardHeader>
            <CardTitle>
              <Text style={styles.cardTitleText}>Tags</Text>
            </CardTitle>
          </CardHeader>
          <CardContent style={styles.cardContent}>
            <View style={styles.tagInputRow}>
              <Input
                value={newTag}
                onChangeText={setNewTag}
                placeholder="Add tag (e.g., Vegetarian, Italian)"
                onSubmitEditing={handleAddTag}
                containerStyle={styles.tagInputContainer}
                style={styles.tagInput}
              />
              <Button onPress={handleAddTag} style={styles.tagAddButton}>
                Add
              </Button>
            </View>
            {tags.length > 0 && (
              <View style={styles.tagsContainer}>
                {tags.map(tag => (
                  <View key={tag} style={styles.tagPill}>
                    <Text style={styles.tagText}>{tag}</Text>
                    <TouchableOpacity
                      onPress={() => handleRemoveTag(tag)}
                      style={styles.tagRemove}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Ionicons name="close" size={12} color={Brand.primaryDark} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <View style={styles.submitRow}>
          <Button variant="outline" onPress={() => router.back()} style={styles.submitButton} disabled={loading}>
            Cancel
          </Button>
          <Button onPress={handleSubmit} style={styles.submitButton} loading={loading} disabled={loading}>
            Save Recipe
          </Button>
        </View>
      </ScrollView>

      <Modal
        visible={showAlertModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAlertModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{alertModalTitle}</Text>
            <Text style={styles.modalDescription}>{alertModalMessage}</Text>
            <View style={styles.modalActions}>
              <Button onPress={() => setShowAlertModal(false)} style={styles.modalButton}>
                OK
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    marginBottom: 24,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 12,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  importedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 16,
    backgroundColor: Brand.primaryLighter,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Brand.primaryLight,
  },
  importedBannerText: {
    flex: 1,
    fontSize: 14,
    color: Neutrals.textSecondary,
  },
  importedBannerDismiss: {
    padding: 4,
  },
  card: {
    marginBottom: 16,
  },
  cardTitleText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  cardContent: {
    gap: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
  },
  rowItem: {
    flex: 1,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    width: '100%',
  },
  listItemInput: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  },
  removeButton: {
    padding: 4,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: Brand.primary,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  addButtonText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  instructionItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
    width: '100%',
  },
  instructionNumber: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Neutrals.surface,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
  },
  instructionNumberText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  instructionInput: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  },
  tagInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  tagInputContainer: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  },
  tagInput: {
    flex: 1,
  },
  tagAddButton: {
    minHeight: 48,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tagPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: Brand.primaryLight,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '600',
    color: Brand.primaryDark,
  },
  tagRemove: {
    padding: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  submitRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  submitButton: {
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
  },
});
